import rootScope from '@lib/rootScope';
import {addCancelButton} from './indexTsx';
import showPeerPopup, {PopupPeerButtonCallbackCheckboxes, PopupPeerOptions} from '@components/popups/peer';
import {ChatType} from '@components/chat/chatType';
import {i18n, LangPackKey} from '@lib/langPack';
import hasRights from '@appManagers/utils/chats/hasRights';
import wrapPeerTitle from '@components/wrappers/peerTitle';
import {Message, MessageMedia} from '@layer';
import {formatFullSentTime} from '@helpers/date';
import tsNow from '@helpers/tsNow';
import showDeleteMegagroupMessagesPopup from '@components/popups/deleteMegagroupMessages';
import getParticipantPeerId from '@appManagers/utils/chats/getParticipantPeerId';
import namedPromises from '@helpers/namedPromises';
import isEphemeralMessage from '@appManagers/utils/messages/isEphemeralMessage';

export default async function showDeleteMessagesPopup(
  peerId: PeerId,
  mids: number[],
  type: ChatType,
  onConfirm?: () => void,
  threadId?: number
) {
  mids = mids.slice();

  const managers = rootScope.managers;

  const {peerTitleElement, isBot, messages} = await namedPromises({
    peerTitleElement: wrapPeerTitle({peerId, threadId, onlyFirstName: true}),
    isBot: managers.appPeersManager.isBot(peerId),
    // scheduled mids belong to a separate storage; getMessageByPeer would read history and
    // return undefined / another chat's message (breaking the megagroup-admin & giveaway checks)
    messages: Promise.all(mids.map((mid) => type === ChatType.Scheduled ?
      managers.appMessagesManager.getScheduledMessageByPeer(peerId, mid) :
      managers.appMessagesManager.getMessageByPeer(peerId, mid)))
  });

  const isEphemeral = !!messages.length && messages.every(isEphemeralMessage);
  const isMegagroup = await managers.appPeersManager.isMegagroup(peerId);
  if(!isEphemeral && isMegagroup && !messages.some((message) => message.pFlags.out)) {
    const participants = await managers.appProfileManager.getParticipants({
      id: peerId.toChatId(),
      filter: {_: 'channelParticipantsAdmins'},
      limit: 100
    });

    const foundAdmin = messages.some((message) => {
      return participants.participants.some((participant) => getParticipantPeerId(participant) === message.fromId);
    });

    if(!foundAdmin) {
      showDeleteMegagroupMessagesPopup({
        messages,
        onConfirm
      });
      return;
    }
  }

  const callback = (
    e: MouseEvent,
    checked: PopupPeerButtonCallbackCheckboxes,
    revoke?: boolean
  ) => {
    onConfirm?.();
    if(type === ChatType.Scheduled) {
      managers.appMessagesManager.deleteScheduledMessages(peerId, mids);
    } else {
      const needRevoke = !!checked.size || revoke;
      if(peerId.isUser() && needRevoke && canRevoke.length !== mids.length) {
        managers.appMessagesManager.deleteMessages(peerId, canRevoke, true);
        managers.appMessagesManager.deleteMessages(peerId, mids.filter((mid) => !canRevoke.includes(mid)), false);
      } else {
        managers.appMessagesManager.deleteMessages(peerId, mids, needRevoke);
      }
    }
  };

  const buttons: PopupPeerOptions['buttons'] = [{
    langKey: 'Delete',
    isDanger: true,
    callback
  }];
  const checkboxes: PopupPeerOptions['checkboxes'] = [];
  let title: LangPackKey, titleArgs: any[], description: LangPackKey, descriptionArgs: any[];
  const isSingleMessage = mids.length === 1;
  if(isSingleMessage) {
    title = 'DeleteSingleMessagesTitle';
  } else {
    title = 'DeleteMessagesTitle';
    titleArgs = [i18n('messages', [mids.length])];
  }

  if(isEphemeral) {
    description = isSingleMessage ? 'AreYouSureDeleteSingleMessage' : 'AreYouSureDeleteFewMessages';
  } else if(isMegagroup) {
    description = isSingleMessage ? 'AreYouSureDeleteSingleMessageMega' : 'AreYouSureDeleteFewMessagesMega';
  } else if(isBot) {
    description = isSingleMessage ? 'AreYouSureDeleteSingleMessageBot' : 'AreYouSureDeleteFewMessagesBot';
  } else {
    description = isSingleMessage ? 'AreYouSureDeleteSingleMessage' : 'AreYouSureDeleteFewMessages';
  }

  let canRevoke: number[] = mids.slice();
  if(isEphemeral) {
    canRevoke = [];
  } else if(peerId === rootScope.myId || type === ChatType.Scheduled || isBot) {

  } else if(peerId.isUser()) {
    canRevoke = canRevoke.filter((mid, idx) => {
      const message = messages[idx];
      const media = (message as Message.message).media;
      if(media?._ === 'messageMediaDice') {
        return false;
      }

      return true;
    });

    if(canRevoke.length === mids.length) {
      checkboxes.push({
        text: 'DeleteMessagesOptionAlso',
        textArgs: [peerTitleElement]
      });
    } else {
      description = isSingleMessage ? 'AreYouSureDeleteSingleMessageOnlyMe' : 'AreYouSureDeleteFewMessagesOnlyMe';

      if(canRevoke.length) {
        checkboxes.push({
          text: 'DeleteMessagesOption'
        });

        description = 'AreYouSureDeleteFewMessagesMixed';
        descriptionArgs = [peerTitleElement];
      }
    }
  } else {
    const chat = await managers.appChatsManager.getChat(peerId.toChatId());

    const _hasRights = hasRights(chat, 'delete_messages');
    if(chat._ === 'chat') {
      canRevoke = _hasRights ? canRevoke : canRevoke.filter((mid, idx) => {
        const message = messages[idx];
        return message.fromId === rootScope.myId;
      });

      if(canRevoke.length) {
        if(canRevoke.length === mids.length) {
          checkboxes.push({
            text: 'DeleteForAll'
          });
        } else {
          checkboxes.push({
            text: 'DeleteMessagesOption'
          });

          description = 'DeleteMessagesTextGroup';
          descriptionArgs = [i18n('messages', [canRevoke.length])];
        }
      }
    } else {
      let foundGiveaway: MessageMedia.messageMediaGiveaway;
      messages.find((message) => {
        return message &&
          (message as Message.message).media?._ === 'messageMediaGiveaway' &&
          !(message as Message.message).fwdFromId &&
          (foundGiveaway = (message as Message.message).media as MessageMedia.messageMediaGiveaway);
      });

      if(foundGiveaway && foundGiveaway.until_date >= tsNow(true)) {
        title = 'BoostingGiveawayDeleteMsgTitle';
        description = 'BoostingGiveawayDeleteMsgText';
        descriptionArgs = [formatFullSentTime(foundGiveaway.until_date, undefined, true)];
      }

      buttons[0].callback = (e, checked) => callback(e, checked, true);
    }
  }

  addCancelButton(buttons);

  showPeerPopup('popup-delete-chat', {
    peerId,
    threadId,
    titleLangKey: title,
    titleLangArgs: titleArgs,
    descriptionLangKey: description,
    descriptionLangArgs: descriptionArgs,
    buttons,
    checkboxes
  });
}
